import { z } from 'zod';
import type { StyleChoice, StyleModel } from './styles/index.ts';
import type { ThemeFile } from './theme.ts';

/**
 * Aparência do sistema (Configuração › Plataforma): nome, logo, símbolo e três cores. Nulo = o
 * padrão da marca. Os tons de apoio (fundo suave do destaque, texto sobre o destaque…) saem das
 * cores escolhidas pela mesma conta na moldura das telas, nunca de outro lugar.
 */

export const PLATFORM_DEFAULTS = {
  appName: 'Ground Up',
  accentColor: '#C4EF2F',
  paperColor: '#F5F6F8',
  inkColor: '#0B0E14',
} as const;

/** Tamanho máximo de cada imagem em data URL (~250 KB de arquivo). */
export const PLATFORM_IMAGE_MAX = 350_000;
export const PLATFORM_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/);
const image = z
  .string()
  .max(PLATFORM_IMAGE_MAX)
  .regex(/^data:image\/(png|jpeg|webp|svg\+xml);base64,[A-Za-z0-9+/=]+$/);

export const platformSettingsInput = z.object({
  appName: z
    .string()
    .trim()
    .max(40)
    .transform((v) => (v === '' ? null : v))
    .nullable(),
  logo: image.nullable(),
  symbol: image.nullable(),
  accentColor: hex.nullable(),
  paperColor: hex.nullable(),
  inkColor: hex.nullable(),
});
export type PlatformSettingsInput = z.input<typeof platformSettingsInput>;
export type PlatformSettings = z.output<typeof platformSettingsInput> & {
  /** Tema do Guia visual (arquivo `base-theme`), montado do zero; vale por cima do estilo. */
  theme: ThemeFile | null;
  /** Estilo pronto escolhido na Configuração › Estilo (ADR 0126); nulo = o de fábrica. */
  style: StyleChoice | null;
  /** Meus modelos do Estúdio (ADR 0133, parte 5). */
  styleModels: StyleModel[];
  updatedAt: string | null;
};

export const EMPTY_PLATFORM_SETTINGS: PlatformSettings = {
  appName: null,
  logo: null,
  symbol: null,
  accentColor: null,
  paperColor: null,
  inkColor: null,
  theme: null,
  style: null,
  styleModels: [],
  updatedAt: null,
};

/**
 * O visual do sistema que vale antes de entrar e antes da primeira tela (ADR 0135): tudo o que a
 * moldura precisa para já abrir com a cara certa, sem os modelos do Estúdio. O logo e o símbolo
 * vêm como endereço da imagem (a API serve cada uma à parte, guardada pelo navegador), nunca a
 * imagem inteira: a primeira tela espera por esta resposta, que precisa ser pequena.
 */
export type PlatformAppearance = Omit<PlatformSettings, 'styleModels' | 'updatedAt'>;

export const EMPTY_PLATFORM_APPEARANCE: PlatformAppearance = {
  appName: null,
  logo: null,
  symbol: null,
  accentColor: null,
  paperColor: null,
  inkColor: null,
  theme: null,
  style: null,
};

/**
 * O visual a partir da aparência completa (depois de gravar na Configuração, a moldura muda na
 * hora, sem buscar de novo). O logo fica como veio: um data URL também serve de endereço de imagem.
 */
export function appearanceOf(s: PlatformSettings): PlatformAppearance {
  const { styleModels: _models, updatedAt: _updated, ...appearance } = s;
  return appearance;
}

function rgb(hexColor: string): [number, number, number] {
  const n = parseInt(hexColor.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

/** Mistura duas cores: 0 = a primeira, 1 = a segunda. */
export function mixColors(a: string, b: string, amount: number): string {
  const x = rgb(a);
  const y = rgb(b);
  return toHex([0, 1, 2].map((i) => x[i]! + (y[i]! - x[i]!) * amount) as [number, number, number]);
}

/** Luminância relativa (WCAG). */
export function luminance(hexColor: string): number {
  const [r, g, b] = rgb(hexColor).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contraste entre duas cores (1 a 21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * As variáveis CSS da moldura (styles.css) a partir das cores escolhidas. Só as que mudam: sem
 * escolha, a cor do styles.css continua valendo. O texto no tom do destaque escurece até ter
 * contraste 4,5:1 com o branco (leitura de link e item marcado).
 */
export function platformCssVars(
  s: Pick<PlatformSettings, 'accentColor' | 'paperColor' | 'inkColor'>,
) {
  const vars: Record<string, string> = {};
  if (s.accentColor) {
    const accent = s.accentColor.toLowerCase();
    let text = accent;
    for (let i = 1; i <= 10 && contrastRatio(text, '#ffffff') < 4.5; i++)
      text = mixColors(accent, '#000000', i * 0.08);
    const soft = mixColors(accent, '#ffffff', 0.86);
    const onAccent = contrastRatio(accent, '#ffffff') >= 3 ? '#ffffff' : '#0e070b';
    Object.assign(vars, {
      '--accent': accent,
      '--brand': accent,
      '--brand-text': text,
      '--brand-soft': soft,
      '--rail-active': soft,
      '--rail-active-text': text,
      '--on-accent': onAccent,
      // O item marcado do menu (lateral ou topo) também é da cor de destaque: sem isto, trocar a
      // cor deixava os botões numa cor e o menu na do tema.
      '--sidebar-active': accent,
      '--sidebar-active-text': onAccent,
    });
  }
  if (s.paperColor) {
    vars['--paper'] = s.paperColor.toLowerCase();
    vars['--rule'] = mixColors(s.paperColor, '#000000', 0.08);
  }
  if (s.inkColor) {
    vars['--ink'] = s.inkColor.toLowerCase();
    vars['--muted'] = mixColors(s.inkColor, '#ffffff', 0.38);
  }
  return vars;
}
